import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { useAppTheme } from '../../contexts/ThemeContext';

export default function LoginScreen({ navigation }) {
  const { colors, spacing, typography, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, typography, radius), [colors]);

  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const failedAttempts = useRef(0);
  const lockoutUntil = useRef(0);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const lockoutIntervalRef = useRef(null);

  function startLockoutCountdown(seconds) {
    lockoutUntil.current = Date.now() + seconds * 1000;
    setLockoutRemaining(seconds);
    if (lockoutIntervalRef.current) clearInterval(lockoutIntervalRef.current);
    lockoutIntervalRef.current = setInterval(() => {
      const remaining = Math.ceil((lockoutUntil.current - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockoutRemaining(0);
        clearInterval(lockoutIntervalRef.current);
        lockoutIntervalRef.current = null;
      } else {
        setLockoutRemaining(remaining);
      }
    }, 1000);
  }

  useEffect(() => {
    return () => { if (lockoutIntervalRef.current) clearInterval(lockoutIntervalRef.current); };
  }, []);

  function validate() {
    const newErrors = {};
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Enter a valid email';
    if (!password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleForgotPassword() {
    if (!email.trim()) {
      Alert.alert('Reset Password', 'Enter your email address above first.');
      return;
    }
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase());
      if (error) throw error;
      Alert.alert('Check Your Email', `A password reset link has been sent to ${email.trim()}.`);
    } catch (err) {
      Alert.alert('Error', err.message || 'Could not send reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  }

  async function handleSignIn() {
    if (Date.now() < lockoutUntil.current) return;
    if (!validate()) return;
    setAuthError('');
    setLoading(true);
    try {
      await signIn({ email: email.trim().toLowerCase(), password });
      failedAttempts.current = 0;
    } catch (err) {
      failedAttempts.current += 1;
      if (failedAttempts.current >= 5) {
        failedAttempts.current = 0;
        startLockoutCountdown(30);
        setAuthError('Too many failed attempts. Try again in 30 seconds.');
      } else {
        setAuthError(err.message || 'Check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <SafeAreaView style={styles.safe}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.flex}
      >
        <ScrollView
          contentContainerStyle={styles.scroll}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.logoBlock}>
            <Text style={styles.logo}>Slate</Text>
            <Text style={styles.logoSub}>Contractor Quoting</Text>
          </View>

          <View style={styles.form}>
            <Text style={styles.heading}>Welcome back</Text>
            <Text style={styles.subheading}>Sign in to your account</Text>

            {authError ? (
              <View style={styles.errorBox}>
                <Text style={styles.errorBoxText}>{authError}</Text>
              </View>
            ) : null}

            <Input
              label="Email"
              value={email}
              onChangeText={(t) => {
                setEmail(t);
                if (errors.email) setErrors((e) => ({ ...e, email: null }));
              }}
              placeholder="you@company.com"
              keyboardType="email-address"
              autoCapitalize="none"
              error={errors.email}
              returnKeyType="next"
            />

            <Input
              label="Password"
              value={password}
              onChangeText={(t) => {
                setPassword(t);
                if (errors.password) setErrors((e) => ({ ...e, password: null }));
              }}
              placeholder="••••••••"
              secureTextEntry={!showPassword}
              error={errors.password}
              returnKeyType="done"
              onSubmitEditing={handleSignIn}
              rightIcon={
                <Text style={styles.showHide}>
                  {showPassword ? 'Hide' : 'Show'}
                </Text>
              }
              onRightIconPress={() => setShowPassword((v) => !v)}
            />

            <Button
              title={lockoutRemaining > 0 ? `Try again in ${lockoutRemaining}s` : 'Sign In'}
              onPress={handleSignIn}
              loading={loading}
              disabled={lockoutRemaining > 0}
              size="lg"
              style={styles.submitBtn}
            />

            <TouchableOpacity
              style={styles.forgotBtn}
              activeOpacity={0.7}
              onPress={handleForgotPassword}
              disabled={resetLoading}
            >
              <Text style={[styles.forgotText, resetLoading && { opacity: 0.5 }]}>
                {resetLoading ? 'Sending reset link...' : 'Forgot password?'}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.footer}>
            <Text style={styles.footerText}>Don't have an account? </Text>
            <TouchableOpacity
              onPress={() => navigation.navigate('SignUp')}
              activeOpacity={0.7}
            >
              <Text style={styles.footerLink}>Create one</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

function createStyles(colors, spacing, typography, radius) {
  return StyleSheet.create({
    safe: {
      flex: 1,
      backgroundColor: colors.background,
    },
    flex: {
      flex: 1,
    },
    scroll: {
      flexGrow: 1,
      paddingHorizontal: spacing.xl,
      paddingBottom: spacing.xxxl,
    },
    logoBlock: {
      marginTop: spacing.xxxl,
      marginBottom: spacing.xxxl + spacing.xl,
      alignItems: 'center',
    },
    logo: {
      fontSize: 42,
      fontWeight: typography.weights.bold,
      color: colors.text,
      letterSpacing: -2,
    },
    logoSub: {
      fontSize: typography.sizes.sm,
      color: colors.textMuted,
      letterSpacing: 1.5,
      textTransform: 'uppercase',
      marginTop: spacing.xs,
    },
    form: {
      flex: 1,
    },
    heading: {
      fontSize: typography.sizes.xxl,
      fontWeight: typography.weights.bold,
      color: colors.text,
      letterSpacing: -0.8,
      marginBottom: spacing.xs,
    },
    subheading: {
      fontSize: typography.sizes.base,
      color: colors.textSecondary,
      marginBottom: spacing.xl + spacing.sm,
    },
    submitBtn: {
      marginTop: spacing.sm,
    },
    forgotBtn: {
      alignItems: 'center',
      marginTop: spacing.base,
      paddingVertical: spacing.sm,
    },
    forgotText: {
      fontSize: typography.sizes.sm,
      color: colors.textMuted,
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: spacing.xxxl,
      paddingBottom: spacing.base,
    },
    footerText: {
      fontSize: typography.sizes.base,
      color: colors.textSecondary,
    },
    footerLink: {
      fontSize: typography.sizes.base,
      color: colors.accent,
      fontWeight: typography.weights.semibold,
    },
    showHide: {
      fontSize: typography.sizes.sm,
      color: colors.accent,
      fontWeight: typography.weights.medium,
    },
    errorBox: {
      backgroundColor: colors.dangerLight,
      borderRadius: radius.md,
      padding: spacing.md,
      marginBottom: spacing.base,
      borderWidth: 1,
      borderColor: colors.danger,
    },
    errorBoxText: {
      color: colors.danger,
      fontSize: typography.sizes.sm,
    },
  });
}
